import { useState, useEffect, useMemo } from 'react';
import { DealFilterOptions } from '@/types';
import MarketCapFilter from './MarketCapFilter';
import SmeStatusFilter from './SmeStatusFilter';
import StageFilter from './StageFilter';
import SmartInvestorFilter from './SmartInvestorFilter';
import DealTypeMultiFilter from './DealTypeMultiFilter';
import DateRangeFilter from './DateRangeFilter';
import StockNameFilter from './StockNameFilter';
import WatchlistMultiFilter from './WatchlistMultiFilter';
import { Disclosure } from '@headlessui/react';
import { ChevronDownIcon } from '@heroicons/react/24/outline';
import Spinner from '@/components/ui/Spinner';

interface DealFilterFormProps {
  filterOptions: DealFilterOptions;
  onSubmit: (filters: Record<string, unknown>) => void;
  isLoading?: boolean;
}

export default function DealFilterForm({ filterOptions, onSubmit, isLoading = false }: DealFilterFormProps) {
  // Memoize the default dates calculation
  const defaultDates = useMemo(() => {
    const endDate = filterOptions.time_period_end || new Date().toISOString().split('T')[0];
    const startDateObj = new Date(endDate);
    startDateObj.setDate(startDateObj.getDate() - 30);
    const startDate = startDateObj.toISOString().split('T')[0];
    return { startDate, endDate };
  }, [filterOptions.time_period_end]);

  const [filters, setFilters] = useState({
    deal_type: '',
    market_cap_min: '',
    market_cap_max: '',
    sme: '',
    time_period_start: defaultDates.startDate,
    time_period_end: defaultDates.endDate,
    esm_stage: '',
    asm_stage: '',
    gsm_stage: '',
    rights: '',
    open_offer: '',
    pref: '',
    smart_investor: '',
    stock_name: '',
    watchlist_ids: '',
  });

  // Only update dates when filterOptions.time_period_end actually changes
  useEffect(() => {
    setFilters(prev => ({
      ...prev,
      time_period_start: defaultDates.startDate,
      time_period_end: defaultDates.endDate
    }));
  }, [defaultDates.startDate, defaultDates.endDate]);

  const handleChange = (e: React.ChangeEvent<HTMLSelectElement | HTMLInputElement>) => {
    const { name, value } = e.target;
    
    // Special handling for date inputs to ensure start date is not after end date
    if (name === 'time_period_start' && value > filters.time_period_end) {
      return; // Don't update if start date would be after end date
    }
    
    if (name === 'time_period_end' && value < filters.time_period_start) {
      // If end date is changed to before start date, also update start date
      setFilters(prev => ({ 
        ...prev, 
        [name]: value,
        time_period_start: value // Set start date to match end date
      }));
      return;
    }
    
    setFilters(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    // Convert string values to appropriate types and filter out empty values
    const processedFilters: Record<string, unknown> = {};
    
    // Process each filter and only include non-empty values
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== null && value !== undefined && value !== '') {
        switch (key) {
          case 'market_cap_min':
          case 'market_cap_max':
            const numValue = Number(value);
            if (!isNaN(numValue)) {
              processedFilters[key] = numValue;
            }
            break;
          case 'sme':
            if (value === 'true') processedFilters[key] = true;
            else if (value === 'false') processedFilters[key] = false;
            break;
          case 'smart_investor':
            if (value === 'true') processedFilters[key] = true;
            else if (value === 'false') processedFilters[key] = false;
            break;
          case 'esm_stage':
          case 'asm_stage':
          case 'gsm_stage':
            // Convert "Yes"/"No" to boolean if needed, or keep as string
            if (value === 'Yes') processedFilters[key] = true;
            else if (value === 'No') processedFilters[key] = false;
            else processedFilters[key] = value; // Keep other string values as-is
            break;
          default:
            // For all other fields (including deal_type and watchlist_ids), include as-is if not empty
            processedFilters[key] = value;
            break;
        }
      }
    });
    
    onSubmit(processedFilters);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-3">
      {/* Primary Filters - Always visible */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
        <StockNameFilter
          value={filters.stock_name}
          onChange={handleChange}
        />
        
        <DealTypeMultiFilter 
          value={filters.deal_type}
          onChange={handleChange}
          options={filterOptions.deal_type}
        />
        
        <WatchlistMultiFilter
          value={filters.watchlist_ids}
          onChange={handleChange}
          watchlists={filterOptions.watchlists || []}
        />
        
        <div className="flex gap-3">
          <div className="w-1/2">
            <SmeStatusFilter 
              value={filters.sme}
              onChange={handleChange}
              name="sme"
            />
          </div>
          <div className="w-1/2">
            <SmartInvestorFilter 
              value={filters.smart_investor}
              onChange={handleChange}
            />
          </div>
        </div>
      </div>
      
      {/* Advanced Filters - Collapsible */}
      <Disclosure>
        {({ open }) => (
          <div>
            <Disclosure.Button className="flex w-full justify-between items-center px-3 py-2 text-sm font-medium text-left text-emerald-700 bg-emerald-500/10 dark:text-emerald-400 rounded-lg hover:bg-emerald-500/15 focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500/40">
              <span>Advanced Filters</span>
              <ChevronDownIcon
                className={`${
                  open ? 'transform rotate-180' : ''
                } w-5 h-5 text-emerald-500`}
              />
            </Disclosure.Button>
            <Disclosure.Panel className="pt-4 pb-2 text-sm text-zinc-500">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Left column */}
                <div className="space-y-3">
                  {/* Date Range */}
                  <DateRangeFilter 
                    startDate={filters.time_period_start}
                    endDate={filters.time_period_end}
                    onChange={handleChange}
                    minDate="2020-01-01"
                    maxDate={new Date().toISOString().split('T')[0]}
                  />
                  
                  {/* Market Cap */}
                  <MarketCapFilter 
                    min={filters.market_cap_min}
                    max={filters.market_cap_max}
                    onChange={handleChange}
                    range={{
                      market_cap_min: filterOptions.market_cap_min,
                      market_cap_max: filterOptions.market_cap_max
                    }}
                  />
                  
                  {/* Surveillance Stages */}
                  <div className="bg-zinc-50 dark:bg-zinc-800/60 p-3 rounded-xl border border-zinc-200 dark:border-zinc-800">
                    <h3 className="text-xs font-medium text-zinc-700 dark:text-zinc-300 mb-2 uppercase tracking-wider">
                      Surveillance Status
                    </h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <StageFilter 
                        name="esm_stage"
                        label="ESM Stage"
                        value={filters.esm_stage}
                        onChange={handleChange}
                        options={filterOptions.esm_stage}
                      />
                      
                      <StageFilter 
                        name="asm_stage"
                        label="ASM Stage"
                        value={filters.asm_stage}
                        onChange={handleChange}
                        options={filterOptions.asm_stage}
                      />
                    </div>
                  </div>
                </div>
                
                {/* Right column */}
                <div className="space-y-3">
                  {/* Deal Types */}
                  <div className="bg-zinc-50 dark:bg-zinc-800/60 p-3 rounded-xl border border-zinc-200 dark:border-zinc-800">
                    <h3 className="text-xs font-medium text-zinc-700 dark:text-zinc-300 mb-2 uppercase tracking-wider">
                      Deal Types
                    </h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <StageFilter 
                        name="rights"
                        label="Rights"
                        value={filters.rights}
                        onChange={handleChange}
                        options={filterOptions.rights}
                      />
                      
                      <StageFilter 
                        name="open_offer"
                        label="Open Offer"
                        value={filters.open_offer}
                        onChange={handleChange}
                        options={filterOptions.open_offer}
                      />
                    </div>
                    
                    <div className="mt-3">
                      <StageFilter 
                        name="pref"
                        label="Preference"
                        value={filters.pref}
                        onChange={handleChange}
                        options={filterOptions.pref}
                      />
                    </div>
                  </div>
                  
                  {/* GSM Stage */}
                  <div className="bg-zinc-50 dark:bg-zinc-800/60 p-3 rounded-xl border border-zinc-200 dark:border-zinc-800">
                    <h3 className="text-xs font-medium text-zinc-700 dark:text-zinc-300 mb-2 uppercase tracking-wider">
                      GSM Status
                    </h3>
                    <div className="space-y-2">
                      <label htmlFor="gsm_stage" className="block text-sm font-medium text-zinc-700 dark:text-zinc-300">GSM Stage</label>
                      <select
                        id="gsm_stage"
                        name="gsm_stage"
                        value={filters.gsm_stage}
                        onChange={handleChange}
                        className="w-full px-3 py-2 text-sm rounded-lg border border-zinc-300 bg-white text-zinc-900 placeholder:text-zinc-400 dark:border-zinc-700 dark:bg-zinc-900 dark:text-white focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-500/20"
                      >
                        <option value="">All Stages</option>
                        <option value="3">Stage 3</option>
                        <option value="exited">Exited</option>
                        <option value="0">Stage 0</option>
                        <option value="2">Stage 2</option>
                        <option value="1">Stage 1</option>
                      </select>
                      <p className="text-xs text-zinc-500 dark:text-zinc-400">
                        Filter by GSM (Graded Surveillance Measure) stage
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </Disclosure.Panel>
          </div>
        )}
      </Disclosure>
      
      <div className="flex justify-end mt-3">
        <button
          type="submit"
          disabled={isLoading}
          className="px-4 py-2 text-sm font-semibold bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500/40 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isLoading ? (
            <div className="flex items-center">
              <Spinner size="sm" />
              <span className="ml-2">Loading...</span>
            </div>
          ) : 'Apply Filters'}
        </button>
      </div>
    </form>
  );
} 